import { z } from "zod";

export const StudyStatusSchema = z.enum([
  "NEW",
  "SCREENED_TITLE_ABSTRACT",
  "SCREENED_FULL_TEXT",
  "INCLUDED",
  "EXCLUDED",
  "EXTRACTED",
]);
export type StudyStatus = z.infer<typeof StudyStatusSchema>;

export const XrModalitySchema = z.enum([
  "HMD_VR",
  "HMD_MR",
  "MOBILE_AR",
  "OTHER",
  "NOT_SPECIFIED",
]);
export type XrModality = z.infer<typeof XrModalitySchema>;

export const DiagnosisSchema = z.enum([
  "STROKE",
  "TBI",
  "BRAIN_TUMOUR",
  "MIXED_ABI",
  "OTHER",
]);
export type Diagnosis = z.infer<typeof DiagnosisSchema>;

export const ParticipantTypeSchema = z.enum([
  "PATIENTS",
  "HEALTHY_CONTROLS",
  "BOTH",
]);
export type ParticipantType = z.infer<typeof ParticipantTypeSchema>;

export const StudyDesignSchema = z.enum([
  "RCT",
  "QUASI_EXPERIMENTAL",
  "CASE_SERIES",
  "SINGLE_CASE",
  "TECHNICAL",
  "OTHER",
]);
export type StudyDesign = z.infer<typeof StudyDesignSchema>;

export const StudySourceSchema = z.enum([
  "PUBMED",
  "EMBASE",
  "CINAHL",
  "SCOPUS",
  "WEB_OF_SCIENCE",
  "IEEE",
  "COCHRANE",
  "EUROPE_PMC",
  "OPENALEX",
  "MANUAL",
  "OTHER",
]);
export type StudySource = z.infer<typeof StudySourceSchema>;

export const StudySchema = z.object({
  id: z.string().uuid(),
  doi: z.string().nullable(),
  title: z.string().min(1),
  abstract: z.string().nullable(),
  authors: z.array(z.string()),
  year: z.number().int().nullable(),
  journal: z.string().nullable(),
  volume: z.string().nullable(),
  issue: z.string().nullable(),
  pages: z.string().nullable(),
  pmid: z.string().nullable(),
  source: StudySourceSchema,
  status: StudyStatusSchema,
  fullTextUrl: z.string().url().nullable(),
  fullTextPath: z.string().nullable(),
  isDuplicate: z.boolean(),
  deduplicatedFromId: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Study = z.infer<typeof StudySchema>;

export const StudyListItemSchema = StudySchema.pick({
  id: true,
  doi: true,
  title: true,
  authors: true,
  year: true,
  journal: true,
  pmid: true,
  source: true,
  status: true,
  isDuplicate: true,
  createdAt: true,
});
export type StudyListItem = z.infer<typeof StudyListItemSchema>;

export const ImportStudySchema = z.object({
  doi: z.string().nullable().optional(),
  title: z.string().min(1),
  abstract: z.string().nullable().optional(),
  authors: z.array(z.string()),
  year: z.number().int().nullable().optional(),
  journal: z.string().nullable().optional(),
  volume: z.string().nullable().optional(),
  issue: z.string().nullable().optional(),
  pages: z.string().nullable().optional(),
  pmid: z.string().nullable().optional(),
  source: StudySourceSchema,
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type ImportStudy = z.infer<typeof ImportStudySchema>;

export const StudyFiltersSchema = z.object({
  reviewType: z.string().optional(),
  xrModality: z.array(XrModalitySchema).optional(),
  diagnosis: z.array(DiagnosisSchema).optional(),
  participantType: z.array(ParticipantTypeSchema).optional(),
  studyDesign: z.array(StudyDesignSchema).optional(),
  yearFrom: z.coerce.number().int().optional(),
  yearTo: z.coerce.number().int().optional(),
  search: z.string().optional(),
  status: StudyStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type StudyFilters = z.infer<typeof StudyFiltersSchema>;
